// Single source of truth for public URLs and per-page titles.
// DocumentHeader appends " | Awasource", so titles here must not include the brand.
// Keep SITE_URL in sync with public/index.html, public/robots.txt and public/sitemap.xml.
// www is canonical: awasource.com and http:// redirect to https://www.awasource.com.

export const SITE_URL = "https://www.awasource.com";

export const DEFAULT_TITLE = "Hire Verified Talent Across Africa | Awasource";

export const DEFAULT_DESCRIPTION =
  "Companies use Awasource to hire talent that has already passed a skills assessment and a psychometric test. Start with one free trial match.";

// Public pages search engines may index. Every other route is noindex.
export const INDEXABLE_PATHS = [
  /^\/$/,
  /^\/features\/?$/,
  /^\/talents\/?$/,
  /^\/pricing\/?$/,
  /^\/team\/?$/,
  /^\/api-integrations\/?$/,
  /^\/partners\/?$/,
  /^\/awa-ai\/?$/,
  /^\/contact-us\/?$/,
  /^\/jobs\/?$/,
  /^\/terms-of-service\/?$/,
  /^\/privacy-policy\/?$/,
  /^\/login\/?$/,
  /^\/register\/?$/,
  /^\/partner-login\/?$/,
];

const home = { name: "Home", path: "/" };

export const PAGE_META = {
  "/": {
    title: "Hire Verified Talent Across Africa",
    description: DEFAULT_DESCRIPTION,
  },
  "/features": {
    title: "Assessments, Matching and Insights",
    description:
      "Role assessments, match scores, verified profiles, and hiring insights. The tools Awasource gives teams so screening takes hours, not weeks.",
    crumbs: [home, { name: "Features", path: "/features" }],
  },
  "/talents": {
    title: "Get Hired for Skills You Can Prove",
    description:
      "Create a free profile, prove your skills with an assessment, and get matched with companies hiring across Africa. Awasource is free for talent.",
    crumbs: [home, { name: "For talent", path: "/talents" }],
  },
  "/pricing": {
    title: "Hiring Credits, Not Subscriptions",
    description:
      "Buy credits for one hiring round, or save with an annual pack. See the cost per role in naira or dollars before you commit.",
    crumbs: [home, { name: "Pricing", path: "/pricing" }],
  },
  "/api-integrations": {
    title: "Assessment API for Your Platform",
    description:
      "Add role-specific psychometric testing to your ATS or HR product with one API. Your candidates and your brand. Awasource runs the engine.",
    crumbs: [home, { name: "Assessment API", path: "/api-integrations" }],
  },
  "/partners": {
    title: "White-Label Candidate Assessments",
    description:
      "Psychometric tests, CV-to-job ranking, and live results under your brand. Built for agencies, HR teams, and hiring platforms.",
    crumbs: [home, { name: "Partners", path: "/partners" }],
  },
  "/awa-ai": {
    title: "Free AI Career Insight Test",
    description:
      "Take the free AWA-AI test. See your work style, your strongest professional traits, and the roles you are naturally suited for.",
    crumbs: [home, { name: "AWA-AI", path: "/awa-ai" }],
  },
  "/jobs": {
    title: "Open Jobs and Gigs in Africa",
    description:
      "Browse open roles and short gigs from companies hiring on Awasource. Get verified with a free assessment, then apply across Africa.",
    crumbs: [home, { name: "Jobs", path: "/jobs" }],
  },
  "/team": {
    title: "The Skills Verification Platform",
    description:
      "Awasource is the skills verification platform for hiring, built in Nigeria. Hire verified talent in Africa, or add our role assessments to your platform by API.",
    crumbs: [home, { name: "About", path: "/team" }],
  },
  "/contact-us": {
    title: "Hiring, Partnerships, and Support",
    description:
      "Questions about hiring, a partnership, or your account? Email info@awasource.com or call the Abuja team and we will get back to you.",
    crumbs: [home, { name: "Contact", path: "/contact-us" }],
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact Awasource",
      url: `${SITE_URL}/contact-us`,
      mainEntity: {
        "@type": "Organization",
        name: "Awasource",
        url: SITE_URL,
        email: "info@awasource.com",
        telephone: "+2347039521917",
        address: {
          "@type": "PostalAddress",
          streetAddress: "1 Adebiyi Mobolaji street, Marcus Poli estate, Kafe District",
          addressLocality: "Abuja",
          postalCode: "900001",
          addressCountry: "NG",
        },
      },
    },
  },
  "/terms-of-service": {
    title: "Terms of Service",
    description:
      "The rules for companies, talent, and partners using Awasource. Read what you agree to when you create an account or buy hiring credits.",
    crumbs: [home, { name: "Terms of Service", path: "/terms-of-service" }],
  },
  "/privacy-policy": {
    title: "How We Handle Your Data",
    description:
      "What personal data Awasource collects from companies, talent, and partners, why we collect it, and how we protect it.",
    crumbs: [home, { name: "Privacy Policy", path: "/privacy-policy" }],
  },
  "/login": {
    title: "Log in",
    description:
      "Log in to your company account to hire, or to your talent account to finish an assessment and check your matches.",
  },
  "/register": {
    title: "Create a Free Account",
    description:
      "Sign up free as a company ready to hire, or as talent ready to get verified and matched with roles across Africa.",
  },
  "/partner-login": {
    title: "Partner Portal Sign In",
    description:
      "Sign in to send assessments, rank candidates by fit, and manage API keys. Your brand stays on the candidate page.",
  },
  "/verify-mail": {
    title: "Verify your email",
    description: "Confirm your email address to finish creating your Awasource account.",
  },
  "/reset-password": {
    title: "Reset your password",
    description: "Request a link to choose a new password for your Awasource account.",
  },
  "/welcome": {
    title: "Registration Received",
    description: "Your registration is in. See what to do next to finish setting up your account.",
  },
  "/waitlist": {
    title: "Join the Waitlist",
    description: "Join the Awasource waitlist and we will tell you when early access opens.",
  },
  "/support": {
    title: "Help and Support",
    description: "Get help with your Awasource account, hiring, or assessments.",
  },
  "/checkout": {
    title: "Checkout",
    description: "Review your Awasource hiring credits and complete payment.",
  },
  "/portal/reveal": {
    title: "Reveal API key",
    description: "Reveal your Awasource partner API secret key.",
  },
  "/portal/accept-invite": {
    title: "Accept partner invite",
    description: "Set up your Awasource partner portal account.",
  },
  "/portal/forgot": {
    title: "Reset partner password",
    description: "Request a reset link for your Awasource partner portal password.",
  },
  "/portal/reset": {
    title: "Set a new partner password",
    description: "Choose a new password for your Awasource partner portal account.",
  },
  "/accept-invite": {
    title: "Accept your invite",
    description: "Accept your invite and join your team on Awasource.",
  },
  "/talent/payment-success": {
    title: "Payment received",
    description: "Your Awasource payment was received.",
  },
};

const PAGE_PATTERNS = [
  {
    re: /^\/partner(\/|$)/,
    meta: {
      title: "Partner portal",
      description: "Manage assessments, candidate fit, API keys, usage, and billing.",
    },
  },
  {
    re: /^\/job-details\/[^/]+$/,
    meta: {
      title: "Job details",
      description: "See the role, location, and how to apply on Awasource.",
    },
  },
  {
    re: /^\/assessment\/[^/]+$/,
    meta: {
      title: "Your assessment",
      description: "Complete your Awasource assessment.",
    },
  },
  {
    re: /^\/enroll\/[^/]+$/,
    meta: {
      title: "Start your assessment",
      description: "Begin your Awasource assessment.",
    },
  },
  {
    re: /^\/auth\/password\/reset-password\//,
    meta: {
      title: "Choose a new password",
      description: "Set a new password for your Awasource account.",
    },
  },
];

export function normalizePath(pathname) {
  const path = String(pathname || "/").split("?")[0].split("#")[0];
  if (path.length > 1) return path.replace(/\/+$/, "");
  return "/";
}

export function getPageMeta(pathname) {
  const path = normalizePath(pathname);
  if (PAGE_META[path]) return PAGE_META[path];
  const match = PAGE_PATTERNS.find((entry) => entry.re.test(path));
  return match ? match.meta : null;
}
